import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KeyRound, Trash2 } from 'lucide-react';
import { createSecret, deleteSecret, listSecrets, rotateSecret } from '@mkcore/platform/schema';
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Mono, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { ApiError, call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { organisationsQuery } from '@/features/crm/queries';
import { ConfirmButton } from '@/components/confirm';

export const Route = createFileRoute('/_app/admin/secrets')({ component: Secrets });

const secretsQuery = queryOptions({ queryKey: ['admin', 'secret', 'list'], queryFn: () => call(listSecrets, {}) });
const invalidate = [['admin', 'secret']];

/** A random value for a webhook or an API key, made in the browser so the API never has to return a secret. */
function randomValue(): string {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  return btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Secrets per client or for the company (CO-0027, CO-0015): write-only; the list says only that a value is set. */
function Secrets() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const me = useMe();
  const canUpdate = useCan('admin:update') && me.tenantWide;
  const canDelete = useCan('admin:delete') && me.tenantWide;
  const q = useQuery({ ...secretsQuery, enabled: me.tenantWide });
  const orgs = useInfiniteQuery(organisationsQuery({ limit: 200 }));
  const orgName = new Map((orgs.data?.pages.flatMap((p) => p.items) ?? []).map((o) => [o.id, o.name] as const));
  const del = useActionMutation(deleteSecret, { invalidate, success: t('secrets.deleted') });
  if (!me.tenantWide) return <ErrorState title={t('secrets.title')} description={t('secrets.tenantWideOnly')} />;
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  return (
    <>
      <PageHeader eyebrow={t('nav.admin')} title={t('secrets.title')} description={t('secrets.description')} />
      {canUpdate && <NewSecret orgs={[...orgName.entries()]} />}
      {!q.data ? (
        <Skeleton className="h-48" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('secrets.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('secrets.name')}</Th>
              <Th>{t('secrets.owner')}</Th>
              <Th>{t('secrets.kind')}</Th>
              <Th>{t('secrets.rotated')}</Th>
              <Th className="text-right">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {q.data.items.map((s) => (
              <Tr key={s.id}>
                <Td>
                  <Mono className="text-xs">{s.name}</Mono>
                  {s.description && <div className="text-xs text-fg-3">{s.description}</div>}
                </Td>
                <Td>{s.organisationId ? (orgName.get(s.organisationId) ?? <Mono className="text-xs">{s.organisationId}</Mono>) : t('secrets.company')}</Td>
                <Td>
                  {s.kind === 'reference' ? (
                    <>
                      <Badge tone="info">{t('secrets.kindReference')}</Badge> <Mono className="text-xs">{s.reference}</Mono>
                    </>
                  ) : (
                    <Badge tone="outline">{t('secrets.kindValue')}</Badge>
                  )}
                </Td>
                <Td className="whitespace-nowrap tabular text-fg-2">{formatDateTime(s.rotatedAt)}</Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-2">
                    {canUpdate && <Rotate id={s.id} name={s.name} kind={s.kind} version={s.version} />}
                    {canDelete && (
                      <ConfirmButton
                        variant="ghost"
                        icon={<Trash2 className="size-4" aria-hidden />}
                        label={t('secrets.delete')}
                        title={t('secrets.deleteTitle', { name: s.name })}
                        description={t('secrets.deleteDescription')}
                        confirmLabel={t('secrets.delete')}
                        onConfirm={() => del.mutateAsync({ input: { id: s.id }, ifMatch: s.version })}
                      />
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </>
  );
}

function ValueFields(props: { kind: 'value' | 'reference'; value: string; setValue: (v: string) => void; generated: boolean; setGenerated: (v: boolean) => void }) {
  const { t } = useTranslation();
  if (props.kind === 'reference')
    return <Field label={t('secrets.reference')}>{(p) => <Input {...p} value={props.value} placeholder="op://MKCore/item/field" onChange={(e) => props.setValue(e.target.value)} />}</Field>;
  return (
    <div className="grid gap-1">
      <div className="flex items-end gap-2">
        <Field label={t('secrets.value')} className="flex-1">
          {(p) => <Input {...p} type={props.generated ? 'text' : 'password'} autoComplete="off" value={props.value} onChange={(e) => (props.setValue(e.target.value), props.setGenerated(false))} />}
        </Field>
        <Button variant="secondary" onClick={() => (props.setValue(randomValue()), props.setGenerated(true))}>
          {t('secrets.generate')}
        </Button>
      </div>
      {props.generated && (
        <p role="status" className="text-xs text-warning">
          {t('secrets.generated')}
        </p>
      )}
    </div>
  );
}

function NewSecret({ orgs }: { orgs: (readonly [string, string])[] }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [name, setName] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [kind, setKind] = useState<'value' | 'reference'>('value');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [generated, setGenerated] = useState(false);
  const [error, setError] = useState('');
  const create = useActionMutation(createSecret, { invalidate, success: t('secrets.created'), silentErrors: true });
  const submit = () =>
    create.mutate(
      { input: { name, organisationId: organisationId || null, description, ...(kind === 'value' ? { value } : { reference: value }) } },
      {
        onSuccess: () => (setName(''), setValue(''), setDescription(''), setGenerated(false), setError('')),
        onError: (e) => setError(e instanceof ApiError ? message(e) : String(e)),
      },
    );
  return (
    <Card className="mb-4 grid gap-3 p-4">
      <h2 className="flex items-center gap-2 font-bold">
        <KeyRound className="size-4" aria-hidden /> {t('secrets.add')}
      </h2>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={t('secrets.name')}>{(p) => <Input {...p} value={name} placeholder="forgejo.webhook" onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label={t('secrets.owner')}>
          {(p) => (
            <NativeSelect {...p} value={organisationId} onChange={(e) => setOrganisationId(e.target.value)}>
              <option value="">{t('secrets.company')}</option>
              {orgs.map(([id, n]) => (
                <option key={id} value={id}>
                  {n}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('secrets.kind')}>
          {(p) => (
            <NativeSelect {...p} value={kind} onChange={(e) => (setKind(e.target.value as 'value' | 'reference'), setValue(''), setGenerated(false))}>
              <option value="value">{t('secrets.kindValue')}</option>
              <option value="reference">{t('secrets.kindReference')}</option>
            </NativeSelect>
          )}
        </Field>
        <Field label={t('secrets.descriptionField')}>{(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
      </div>
      <ValueFields kind={kind} value={value} setValue={setValue} generated={generated} setGenerated={setGenerated} />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div>
        <Button loading={create.isPending} disabled={!name || !value} onClick={submit}>
          {t('common.save')}
        </Button>
      </div>
    </Card>
  );
}

function Rotate(props: { id: string; name: string; kind: 'value' | 'reference'; version: number }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(props.kind);
  const [value, setValue] = useState('');
  const [generated, setGenerated] = useState(false);
  const [error, setError] = useState('');
  const rotate = useActionMutation(rotateSecret, { invalidate, success: t('secrets.rotatedDone'), silentErrors: true });
  if (!open)
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {t('secrets.rotate')}
      </Button>
    );
  return (
    <div className="grid min-w-72 gap-2 text-left" role="group" aria-label={t('secrets.rotateTitle', { name: props.name })}>
      <NativeSelect aria-label={t('secrets.kind')} value={kind} onChange={(e) => (setKind(e.target.value as 'value' | 'reference'), setValue(''), setGenerated(false))}>
        <option value="value">{t('secrets.kindValue')}</option>
        <option value="reference">{t('secrets.kindReference')}</option>
      </NativeSelect>
      <ValueFields kind={kind} value={value} setValue={setValue} generated={generated} setGenerated={setGenerated} />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          loading={rotate.isPending}
          disabled={!value}
          onClick={() =>
            rotate.mutate(
              { input: { id: props.id, expectedVersion: props.version, ...(kind === 'value' ? { value } : { reference: value }) } },
              { onSuccess: () => (setOpen(false), setValue('')), onError: (e) => setError(e instanceof ApiError ? message(e) : String(e)) },
            )
          }
        >
          {t('common.save')}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}
